import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Pagination from '../components/Pagination';
import { getAllContractorsAdmin, verifyContractor, setFeaturedContractor } from '../api/admin';
import { ContractorAdminDto, PagedResult } from '../types/admin';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const AdminContractorsPage: React.FC = () => {
  const [result, setResult] = useState<PagedResult<ContractorAdminDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [filterVerified, setFilterVerified] = useState<boolean | undefined>(undefined);
  const [filterFeatured, setFilterFeatured] = useState<boolean | undefined>(undefined);
  const [filterTier, setFilterTier] = useState<number | undefined>(undefined);
  const [pageSize, setPageSize] = useState(20);

  // Committed search state
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedVerified, setAppliedVerified] = useState<boolean | undefined>(undefined);
  const [appliedFeatured, setAppliedFeatured] = useState<boolean | undefined>(undefined);
  const [appliedTier, setAppliedTier] = useState<number | undefined>(undefined);
  const [appliedPageSize, setAppliedPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchContractors = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllContractorsAdmin({
        searchTerm: appliedSearch || undefined,
        isVerified: appliedVerified,
        isFeatured: appliedFeatured,
        tier: appliedTier,
        page,
        pageSize: appliedPageSize,
      });
      setResult(data);
    } catch {
      setError('Failed to load contractors.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, appliedVerified, appliedFeatured, appliedTier, appliedPageSize]);

  useEffect(() => {
    fetchContractors(currentPage);
  }, [fetchContractors, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput);
    setAppliedVerified(filterVerified);
    setAppliedFeatured(filterFeatured);
    setAppliedTier(filterTier);
    setAppliedPageSize(pageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVerify = async (id: number, isVerified: boolean, status?: string) => {
    try {
      await verifyContractor(id, isVerified, status);
      await fetchContractors(currentPage);
    } catch {
      setError('Failed to update contractor.');
    }
  };

  const handleFeatured = async (id: number, isFeatured: boolean) => {
    try {
      await setFeaturedContractor(id, isFeatured);
      setResult(prev => prev
        ? { ...prev, items: prev.items.map(c => c.id === id ? { ...c, isFeatured } : c) }
        : prev
      );
    } catch {
      setError('Failed to update featured status.');
    }
  };

  const contractors = result?.items ?? [];

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-primary hover:underline text-sm">Admin</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Contractors</h1>
        </div>

        {/* Search and filter bar */}
        <form onSubmit={handleSearch} className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Company or email"
              className="w-full px-3 py-2 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Status</label>
            <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
              <input
                type="checkbox"
                checked={filterVerified === true}
                onChange={e => setFilterVerified(e.target.checked ? true : undefined)}
              />
              Verified only
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
              <input
                type="checkbox"
                checked={filterFeatured === true}
                onChange={e => setFilterFeatured(e.target.checked ? true : undefined)}
              />
              Featured only
            </label>
          </div>

          <div>
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Tier</label>
            <select
              value={filterTier ?? ''}
              onChange={e => setFilterTier(e.target.value ? Number(e.target.value) : undefined)}
              className="px-3 py-2 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All tiers</option>
              <option value="1">Tier 1</option>
              <option value="2">Tier 2</option>
              <option value="3">Tier 3</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1">Per page</label>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="px-3 py-2 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <button
            type="submit"
            className="px-5 py-2 bg-primary text-on-primary rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Search
          </button>
        </form>

        {error && <p className="text-error mb-4">{error}</p>}

        {result && (
          <p className="text-sm text-on-surface-variant mb-3">
            {result.totalCount} contractor{result.totalCount !== 1 ? 's' : ''} found
          </p>
        )}

        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">Loading...</div>
          ) : contractors.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">No contractors found.</div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-surface-container">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Company</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Email</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Location</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Rating</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Verified</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Featured</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Status</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {contractors.map(contractor => (
                  <tr key={contractor.id} className="hover:bg-surface-container-lowest">
                    <td className="py-3 px-4 text-sm font-medium text-on-surface">{contractor.companyName}</td>
                    <td className="py-3 px-4 text-sm text-on-surface-variant">{contractor.contactEmail}</td>
                    <td className="py-3 px-4 text-sm text-on-surface-variant">{contractor.city}, {contractor.province}</td>
                    <td className="py-3 px-4 text-sm text-center text-on-surface-variant">
                      {contractor.averageRating.toFixed(1)} ({contractor.reviewCount})
                    </td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={contractor.isVerified}
                        onChange={(e) => handleVerify(contractor.id, e.target.checked, contractor.status)}
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={contractor.isFeatured}
                        onChange={(e) => handleFeatured(contractor.id, e.target.checked)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={contractor.status}
                        onChange={(e) => handleVerify(contractor.id, contractor.isVerified ?? false, e.target.value)}
                        className="p-1 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Approved">Approved</option>
                        <option value="Probationary">Probationary</option>
                        <option value="Blacklisted">Blacklisted</option>
                        <option value="Under Review">Under Review</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <Link
                        to={`/admin/contractors/${contractor.id}`}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline font-medium"
                      >
                        <span className="material-symbols-outlined text-[14px]">manage_accounts</span>
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {result && result.totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={result.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminContractorsPage;
